import PropTypes from 'prop-types';
import { makeStyles, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import InfoIcon from '@material-ui/icons/Info';
import { useDispatch, useSelector } from 'react-redux';
import { changeTrip } from '../../../../../../../store/slices/trip';
import getCurrencySymbol from '../../../../../../../helpers/currencySymbol';

const tooltipStyles = makeStyles((theme) => ({
  arrow: {
    color: theme.palette.common.white,
  },
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontSize: 14,
    boxShadow: theme.shadows[1],
  },
}));

const SeatMapPassengers = ({
  seatMap, flight, selectedTravelerIndex, setSelectedTravelerIndex,
}) => {
  const classes = tooltipStyles();
  const { passengers } = useSelector((state) => state.trip);
  const { currencyCode } = useSelector((state) => state.tripSearch);
  const currencySymbol = getCurrencySymbol(currencyCode);
  const dispatch = useDispatch();
  const removeSelection = (passengerIndex) => {
    dispatch(changeTrip({
      field: 'passengers',
      value: {
        ...passengers,
        [passengerIndex]: {
          ...passengers[passengerIndex],
          travelerPreferences: {
            ...passengers[passengerIndex].travelerPreferences,
            flightSeatNumbers: {
              ...passengers[passengerIndex].travelerPreferences.flightSeatNumbers,
              [flight.flightNumber]: null,
            },
          },
        },
      },
    }));
  };

  return (
    <div style={{ minHeight: 400 }}>
      {seatMap && !seatMap.isAnySeatsAvailable ? (
        <div className="text-sm">
          There are no seats available for your selected fare class
          {' '}
          {flight.cabinClassName}
          {' '}
          Cabin
        </div>
      )
        : (
          <>
            <div className="sm:text-xl font-bold mb-2">
              Seat selection:
              <Tooltip
                arrow
                classes={classes}
                title="Although the seats selected were available when you started the assignment process, there is a chance a seat was assigned to another customer prior to the completion of your transaction.  If seats are unavailable on the displayed seat map, you can obtain a seat assignment upon check-in.  Airlines accommodate the seating requirements of customers with certain types of disabilities. This could result in the occasional need to change another individual's pre-assigned seat, with bulkhead seats being particularly subject to reassignment. We appreciate your cooperation in these special circumstances."
                style={{ opacity: 1 }}
              >
                <InfoIcon style={{ fontSize: '1rem', marginTop: -10 }} className="text-theme" />
              </Tooltip>
            </div>
            {passengers && Object.values(passengers).map((p, index) => (
              !p.isLapInfant && (
              <div
                key={`${index.toString()}`}
                className={clsx({
                  'sm:grid grid-cols-2 text-sm p-2 cursor-pointer outline-none border': true,
                  'mt-2': index > 0,
                  'border-dashed border-current': index === selectedTravelerIndex,
                  'border-transparent': index !== selectedTravelerIndex,
                })}
                role="button"
                onClick={() => setSelectedTravelerIndex(index)}
                onKeyDown={() => setSelectedTravelerIndex(index)}
                tabIndex="0"
              >
                <div className="leading-tight">
                  <div className="font-bold">
                    Traveler
                    {' '}
                    {index + 1}
                  </div>
                  {p.travelerPreferences && p.travelerPreferences.flightSeatNumbers
                    && p.travelerPreferences.flightSeatNumbers[flight.flightNumber]
                    ? (
                      <>
                        <div>
                          Seat:
                          {' '}
                          {p.travelerPreferences.flightSeatNumbers[flight.flightNumber].seatNumber}
                        </div>
                        <a
                          className="text-theme cursor-pointer sm:whitespace-no-wrap"
                          role="button"
                          tabIndex="0"
                          onKeyDown={() => removeSelection(index)}
                          onClick={() => removeSelection(index)}
                        >
                          Remove selection
                        </a>
                      </>
                    ) : <span className="sm:whitespace-no-wrap">Seat: Undefined</span>}
                </div>
                <div className="text-center">
                  {p.travelerPreferences && p.travelerPreferences.flightSeatNumbers
                    && p.travelerPreferences.flightSeatNumbers[flight.flightNumber]
                    ? `${currencySymbol}${p.travelerPreferences.flightSeatNumbers[flight.flightNumber].price || 0}` : ''}
                </div>
              </div>
              )
            ))}
          </>
        )}
    </div>
  );
};

SeatMapPassengers.propTypes = {
  seatMap: PropTypes.shape({
    isAnySeatsAvailable: PropTypes.bool.isRequired,
  }).isRequired,
  selectedTravelerIndex: PropTypes.number.isRequired,
  setSelectedTravelerIndex: PropTypes.func.isRequired,
  flight: PropTypes.shape({
    cabinClassName: PropTypes.string,
    departDateTime: PropTypes.string,
    destinationAirportCode: PropTypes.string,
    destinationAirportFullName: PropTypes.string,
    flightNumber: PropTypes.string,
    originAirportCode: PropTypes.string,
    originAirportFullName: PropTypes.string,
  }).isRequired,
};

export default SeatMapPassengers;
